import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { makeStyles } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { Theme } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import Button from '@mui/material/Button';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import ProductLayout from '../../../../../../layout/Product';
import SchemaCompanyName from '../../../../../../components/_schema/SchemaCompanyName';
import GraphQLTable from '../../../../../../components/_table/GraphQLTable';
import { IDropshippingRequestProductSchema } from '../../../../../../../graphql/type/dropshippingRequestProduct';
import {
  IDropshippingRequestProductQueryData,
  IDropshippingRequestProductVars,
  useDropshippingRequestProductQuery,
} from '../../../../../../../graphql/query/dropshippingRequestProduct/useDropshippingRequestProductQuery';
import useNotification from '../../../../../../components/_hook/useNotification';
import useRouter from '../../../../../../components/_hook/useRouter';
import Context from '../../../../../../context';
import Helmet from '../../../../../../components/Helmet';
import { useBatchUpdateCustomPriceDropshippingProductRequestMutation } from '../../../../../../../graphql/mutation/dropshippingRequestProduct/batchUpdateCustomPriceDropshippingProductRequestMutation';
import CircularProgress from '../../../../../../components/_loading/CircularProgress';
import NotFound from '../../../../../../components/_error/NotFound';
import { useDropshippingRequestCompanyQuery } from '../../../../../../../graphql/query/dropshippingRequestCompany/useDropshippingRequestCompanyQuery';
import SelectDropshippingRequestProductRelatedProduct from './_module/SelectDropshippingRequestProductRelatedProduct';
import DropshippingDropshipperApprovedDetailTableItem from './DropshippingDropshipperApprovedDetailTableItem';
import ButtonBatch from '../../../../../../components/_button/ButtonBatch';
import { useAllowDropshippingProductRequestsMutation } from '../../../../../../../graphql/mutation/dropshippingRequestProduct/allowDropshippingProductRequestsMutation';
import { useDenyDropshippingProductRequestsMutation } from '../../../../../../../graphql/mutation/dropshippingRequestProduct/denyDropshippingProductRequestsMutation';
import ButtonPublish from '../../../../../../components/_button/ButtonPublish';
import ButtonUnpublish from '../../../../../../components/_button/ButtonUnpublish';
import { IProductSchema } from '../../../../../../../graphql/type/product';
import ProductDropshippingDropshipperCustomPriceBatchUpdate from './_module/ProductDropshippingDropshipperCustomPriceBatchUpdate';
import DialogConfirm from '../../../../../../components/_dialog/DialogConfirm';
import { useSendCustomPriceUpdatedNotifyEmailDropshippingProductRequestMutation } from '../../../../../../../graphql/mutation/dropshippingRequestProduct/sendCustomPriceUpdatedNotifyEmailDropshippingProductRequestMutation';

const useStyles = makeStyles<Theme>((theme: Theme) => ({
  infoContainer: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingTop: theme.spacing(2),
  },
}));

function DropshippingDropshipperApprovedDetail() {
  interface IItem extends IDropshippingRequestProductSchema {
    key: string;
    variationId?: string;
    customPriceCurrency?: string;
    customPriceFinal?: number;
    customPriceByPricePercentage?: number;
    customPriceByDropshipperPricePercentage?: number;
    variation?: IProductSchema['variations'][0];
  }

  const { t } = useTranslation();
  const { notification } = useNotification();
  const classes = useStyles();
  const { params } = useRouter();
  const { company: contextCompany } = useContext(Context);
  const [tableRefreshKey, setTableRefreshKey] = useState<number>(0);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedItems, setSelectedItems] = useState<IItem[]>([]);
  const [isShowDialogBatch, setIsShowDialogBatch] = useState(false);
  const { companyID, docID } = params;
  const [itemsChanged, setItemsChanged] = useState<IItem[]>([]);
  const [isShowEmailNotifyDialog, setIsShowEmailNotifyDialog] = useState(false);

  const updatedDropshippingProductRequestFragment = `
                _id
                status
				customPrices {
				  currency
                  final
                  variations {
                    variationID
                    prices {
                      currency
                      final
                    }
                  }
                }
  `;

  const [
    batchUpdateCustomPriceDropshippingProductRequestMutation,
    { loading: isBatchUpdatingPriceDropshippingRequestProduct },
  ] = useBatchUpdateCustomPriceDropshippingProductRequestMutation(
    `updatedDropshippingProductRequests {
				${updatedDropshippingProductRequestFragment}
			}`,
    {
      onCompleted: (data) => {
        if (
          data &&
          data.batchUpdateCustomPriceDropshippingProductRequest &&
          data.batchUpdateCustomPriceDropshippingProductRequest
            .updatedDropshippingProductRequests
        ) {
          notification.success(
            t('__item___updated', {
              item: `${
                data.batchUpdateCustomPriceDropshippingProductRequest
                  .updatedDropshippingProductRequests.length
              } ${t('product', {
                count:
                  data.batchUpdateCustomPriceDropshippingProductRequest
                    .updatedDropshippingProductRequests.length,
              })}`,
            })
          );
          if (
            data.batchUpdateCustomPriceDropshippingProductRequest
              .updatedDropshippingProductRequests.length
          ) {
            setIsShowEmailNotifyDialog(true);
          }
        }
        setIsEditing(false);
      },
      onError: (e) => {
        setIsEditing(false);
        notification.error(e.message);
      },
    }
  );

  const [
    allowDropshippingProductRequestsMutation,
  ] = useAllowDropshippingProductRequestsMutation(
    `updatedDropshippingProductRequests {
				${updatedDropshippingProductRequestFragment}
			}`,
    {
      onCompleted: () => {
        notification.success(
          t('__item___updated', {
            item: `${selectedItems.length} ${t('request', {
              count: selectedItems.length,
            })}`,
          })
        );
        setSelectedItems([]);
      },
      onError: (e) => {
        notification.error(e.message);
      },
    }
  );

  async function onClickAllow() {
    await allowDropshippingProductRequestsMutation({
      variables: { _ids: selectedItems.map((item) => item._id) },
    });
  }

  const [
    denyDropshippingProductRequestsMutation,
  ] = useDenyDropshippingProductRequestsMutation(
    `updatedDropshippingProductRequests {
				${updatedDropshippingProductRequestFragment}
			}`,
    {
      onCompleted: () => {
        notification.success(
          t('__item___updated', {
            item: `${selectedItems.length} ${t('request', {
              count: selectedItems.length,
            })}`,
          })
        );
        setIsShowEmailNotifyDialog(true);
      },
      onError: (e) => {
        notification.error(e.message);
      },
    }
  );

  async function onClickDeny() {
    await denyDropshippingProductRequestsMutation({
      variables: { _ids: selectedItems.map((item) => item._id) },
    });
  }

  async function onClickSave() {
    await batchUpdateCustomPriceDropshippingProductRequestMutation({
      variables: {
        batchUpdateCustomPriceDropshippingProductRequests: itemsChanged.map(
          (item) => ({
            requestId: item._id,
            variationId: item.variationId,
            customPriceCurrency: item.customPriceCurrency,
            customPriceFinal: item.customPriceFinal,
            customPriceByPricePercentage: item.customPriceByPricePercentage,
            customPriceByDropshipperPricePercentage:
              item.customPriceByDropshipperPricePercentage,
          })
        ),
      },
    });
  }

  const { data, loading } = useDropshippingRequestCompanyQuery(
    `
		_id
		from_company_id
		to_company_id
		fromCompanyData {
			_id
			origin
			verification
			slug
			name {
				la
				text
			}
		}
		status 
	`,
    {
      variables: {
        filter: {
          toCompanyId: contextCompany._id,
          _id: docID as string,
        },
      },
    }
  );

  const [
    sendCustomPriceUpdatedNotifyEmailDropshippingProductRequestMutation,
  ] = useSendCustomPriceUpdatedNotifyEmailDropshippingProductRequestMutation(
    'isSuccess',
    {
      onCompleted: () => {
        notification.success(t('success'));
        setIsShowEmailNotifyDialog(false);
        setIsEditing(false);
        setSelectedItems([]);
        setItemsChanged([]);
      },
      onError: (e) => {
        notification.error(e.message);
      },
    }
  );

  async function emailNotifyDropshipper() {
    if (selectedItems.length || itemsChanged.length) {
      await sendCustomPriceUpdatedNotifyEmailDropshippingProductRequestMutation(
        {
          variables: {
            _ids: selectedItems.length
              ? selectedItems.map((selectedItem) => selectedItem._id)
              : itemsChanged.map((item) => item._id),
          },
        }
      );
    }
  }

  const dropshippingRequestCompany = data
    ? data.dropshippingRequestCompanies.items[0]
    : null;
  if (loading) return <CircularProgress center color="primary" />;
  if (!dropshippingRequestCompany) {
    return <NotFound />;
  }

  return (
    <>
      <Helmet
        title={`${t('dropshipper')} ${t('price_management')} | ${t('product')}`}
      />
      <DialogConfirm
        title={t(
          'are_you_wish_email_inform_dropshipper_that_dropshipping_product_updated'
        )}
        open={isShowEmailNotifyDialog}
        onClose={() => {
          setIsShowEmailNotifyDialog(false);
          setIsEditing(false);
          setSelectedItems([]);
          setItemsChanged([]);
        }}
        onConfirm={emailNotifyDropshipper}
      />
      <Dialog
        open={isShowDialogBatch}
        maxWidth="md"
        fullWidth
        onClose={() => setIsShowDialogBatch(false)}
      >
        <ProductDropshippingDropshipperCustomPriceBatchUpdate
          batchUpdateCustomPriceDropshippingProductRequests={selectedItems.map(
            (selectedItem) => ({
              requestId: selectedItem._id,
              variationId: selectedItem.variationId,
            })
          )}
          onCompleted={() => {
            setIsShowEmailNotifyDialog(true);
            setIsShowDialogBatch(false);
          }}
          onCancel={() => setIsShowDialogBatch(false)}
        />
      </Dialog>
      <Grid container>
        <Grid item container className={classes.infoContainer} spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h5" align="center">
              {t('approved_dropshipper')}:&nbsp;
              <b>
                {dropshippingRequestCompany.fromCompanyData && (
                  <SchemaCompanyName
                    company={dropshippingRequestCompany.fromCompanyData}
                    showVerification
                  />
                )}
              </b>
            </Typography>
          </Grid>
        </Grid>
        <Grid container spacing={1}>
          {isEditing ? (
            <Grid item>
              <Button
                disabled={isBatchUpdatingPriceDropshippingRequestProduct}
                variant="contained"
                color="primary"
                onClick={onClickSave}
                startIcon={<SaveIcon />}
              >
                {t('save')}
              </Button>
            </Grid>
          ) : (
            <>
              <Grid item>
                <Button
                  color="primary"
                  variant="contained"
                  startIcon={<EditIcon />}
                  onClick={() => {
                    setSelectedItems([]);
                    setIsEditing(true);
                  }}
                >
                  {t('custom_price')}
                </Button>
              </Grid>
            </>
          )}
        </Grid>
        <Grid item xs={12}>
          <GraphQLTable<
            IDropshippingRequestProductQueryData,
            IItem,
            IDropshippingRequestProductVars
          >
            useCheckbox={!isEditing}
            refreshKey={tableRefreshKey}
            actionButtons={[
              <ButtonPublish
                key="allow"
                variant="contained"
                color="primary"
                size="small"
                text={t('allow')}
                onClick={onClickAllow}
              />,
              <ButtonUnpublish
                key="deny"
                color="primary"
                variant="contained"
                size="small"
                text={t('deny')}
                onClick={onClickDeny}
              />,
              <ButtonBatch
                key="batch"
                color="primary"
                variant="contained"
                size="small"
                text={`${t('batch')} ${t('update')} ${t('custom_price')}`}
                onClick={() => setIsShowDialogBatch(true)}
              />,
            ]}
            select={`
							_id
							source_product_id
							sourceProductData {
								_id
								company
								name {
									la
									text
								}
								prices {
									final
									currency
								}
								variations { 
                                  id
                                  attributes
                                  dropshipping {
                                    prices {
                                      currency
                                      final
                                    }
                                  }
                                }
                                dropshipping {
                                  prices {
                                    currency
                                    final
                                  }
                                }
							}
							customPrices {
                              currency
                              final
                              variations {
                                variationID
                                prices {
                                  currency
                                  final
                                }
                              }
                            }
							status 
					 `}
            useQuery={useDropshippingRequestProductQuery}
            renderHeader={[
              {
                field: 'name.text',
                label: t('product'),
                align: 'left',
              },
              {
                field: 'current_displayed_sales_price',
                label: t('current_displayed_sales_price'),
                align: 'center',
              },
              {
                field: 'current_sales_price',
                label: t('current_sales_price'),
                align: 'center',
              },
              {
                field: 'sales_price_to_dropshipper',
                label: t('sales_price_to_dropshipper'),
                align: 'center',
              },
              {
                field: 'custom_price',
                label: t('custom_price'),
                align: 'center',
              },
              {
                field: 'status',
                label: t('status'),
                align: 'center',
              },
            ]}
            renderItem={(item) => (
              <DropshippingDropshipperApprovedDetailTableItem
                item={item}
                setTableRefreshKey={setTableRefreshKey}
                isEditable={isEditing}
                onChange={(tempItem) => {
                  let isExists = false;
                  const tempItemsChanged = itemsChanged.map((itemChanged) => {
                    if (
                      tempItem._id === itemChanged._id &&
                      tempItem.variationId === itemChanged.variationId
                    ) {
                      isExists = true;
                      return tempItem;
                    }

                    return itemChanged;
                  });

                  if (!isExists) {
                    tempItemsChanged.push(tempItem);
                  }

                  setItemsChanged(tempItemsChanged);
                }}
              />
            )}
            onItemSelected={setSelectedItems}
            selectedItems={selectedItems}
            filterFields={{
              _id: {
                label: t('product'),
                component: SelectDropshippingRequestProductRelatedProduct,
                componentProps: {
                  filter: {
                    fromCompanyId: dropshippingRequestCompany.from_company_id,
                    toCompanyId: dropshippingRequestCompany.to_company_id,
                  },
                  menuPosition: 'fixed',
                },
                onChange: (option) => (option ? option.value : null),
                size: 12,
              },
            }}
            defaultFilterOptions={{
              fromCompanyId: dropshippingRequestCompany.from_company_id,
              toCompanyId: dropshippingRequestCompany.to_company_id,
            }}
            title={t('requested_product_dropshipping_list')}
            defaultSort="createdTime"
            defaultSortOrder={-1}
            itemKey="key"
            filterData={(tempData) => {
              const dataWithVariations: IItem[] = [];

              tempData.forEach((item) => {
                const itemCustomPrices = item.customPrices || {};
                dataWithVariations.push({
                  ...item,
                  ...{
                    key: item._id,
                    customPriceCurrency:
                      itemCustomPrices && itemCustomPrices.currency
                        ? itemCustomPrices.currency
                        : '',
                    customPriceFinal:
                      itemCustomPrices && itemCustomPrices.final
                        ? itemCustomPrices.final
                        : 0,
                    customPriceByPricePercentage: undefined,
                    customPriceByDropshipperPricePercentage: undefined,
                  },
                });

                if (
                  item.sourceProductData &&
                  item.sourceProductData.variations &&
                  item.sourceProductData.variations.length
                ) {
                  item.sourceProductData.variations.forEach((variation) => {
                    const itemCustomPriceVariations =
                      itemCustomPrices.variations || [];
                    const itemCustomPriceVariation = itemCustomPriceVariations.find(
                      (tempItemCustomPriceVariation) =>
                        tempItemCustomPriceVariation.variationID ===
                        variation.id
                    );

                    dataWithVariations.push({
                      ...item,
                      ...{
                        variation,
                        key: `${item._id}$$${variation.id}`,
                        variationId: variation.id,
                        customPriceFinal:
                          itemCustomPriceVariation &&
                          itemCustomPriceVariation.prices &&
                          itemCustomPriceVariation.prices.final
                            ? itemCustomPriceVariation.prices.final
                            : 0,
                        customPriceByPricePercentage: undefined,
                        customPriceByDropshipperPricePercentage: undefined,
                      },
                    });
                  });
                }
              });

              return dataWithVariations;
            }}
          />
        </Grid>
      </Grid>
    </>
  );
}

export default () => (
  <ProductLayout main={<DropshippingDropshipperApprovedDetail />} />
);
